import {
  Skeleton,
  Stack,
  IconButton,
  Box,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  Text,
} from '@chakra-ui/react';

import { mdiClose } from '@mdi/js';
import { Select as ReactSelect } from 'chakra-react-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import MediaCard from 'features/common/components/MediaCard/MediaCard';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { GroupDataApp } from 'features/user-group/user-group-list/components/groupListTableHelpers';
import { useUpdateOrgGroupRole } from 'features/user-group/user-group-details/api/useUpdateOrgGroup';
import {
  buildUpdatedRolesForRemoval,
  buildUpdatedRolesForRoleChange,
  getTenantId,
} from 'features/user-group/user-group-tabs/utils/updateRoles';
import { useTransformedTenants } from 'features/user-group/user-group-tabs/hooks/useTransform';
import { useSearchOnList } from 'features/common/hooks/search/useSearchOnList';

interface RoleOption {
  value: string;
  label: string;
}

interface ProductAccessRow {
  id: string;
  title: string;
  subTitle: string;
  productCode: string;
  icon: { iconUrl: string };
  index?: number;
  roles: RoleOption[];
  role?: string;
  accessType?: RoleOption;
}
interface ProductAccess extends ProductAccessRow {
  role: string;
}

const ORG_GROUP_APP_SEARCH_FIELDS: Array<'subTitle' | 'title' | 'productCode'> = [
  'subTitle',
  'title',
  'productCode',
];

export const OrgGroupApps = ({
  isLoading,
  tenantList,
  searchValue,
}: {
  isLoading: boolean;
  tenantList: GroupDataApp[];
  searchValue: string;
}) => {
  const [productAccessRowState, setProductAccessRowState] = useState<
    ProductAccessRow[]
  >([]);
  const [selectedRemoveIndex, setSelectedRemoveIndex] = useState<number | null>(
    null,
  );
  const [updatingTenantId, setUpdatingTenantId] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { mutate: updateGroup } = useUpdateOrgGroupRole();
  const toast = useToast();
  const { transformedTenants } = useTransformedTenants();
  const { id: groupId } = useParams<{ id: string }>();

  const productAccessRows = useMemo(() => {
    const productIds = tenantList?.map((p) => p.tenantId) || [];

    const filteredProducts: ProductAccessRow[] =
      transformedTenants?.filter((tenant) => productIds.includes(tenant.id)) ||
      [];

    const productAccess: ProductAccess[] =
      filteredProducts?.map((tenant) => {
        const group = tenantList.find((g) => g.tenantId === tenant.id);
        return { ...tenant, role: group?.role || '' };
      }) || [];

    return (
      productAccess?.map((product: ProductAccess, index) => {
        const { id, title, subTitle, productCode, icon, roles, role } = product;
        return {
          id,
          title,
          subTitle,
          productCode,
          icon,
          index,
          roles,
          role,
        };
      }) || []
    );
  }, [tenantList, transformedTenants]);

  const filteredProductAccessRows = useSearchOnList(
    productAccessRows,
    ORG_GROUP_APP_SEARCH_FIELDS,
    searchValue,
  );

  useEffect(() => {
    if (filteredProductAccessRows) {
      setProductAccessRowState(
        filteredProductAccessRows.map((row: ProductAccessRow) => ({
          ...row,
          accessType: row.roles?.find(
            (r: RoleOption) => r.value === row.role,
          ) ||
            row.roles?.[0] || { value: '', label: '' },
        })),
      );
    }
  }, [filteredProductAccessRows]);

  const onSelectAccessHandler = (
    selectionIndex: number,
    selection: { value: string; label: string } | null,
  ) => {
    if (!selection) return;
    setProductAccessRowState((prev) =>
      prev.map((row, idx) =>
        idx === selectionIndex ? { ...row, accessType: selection } : row,
      ),
    );
    const row = productAccessRowState[selectionIndex];
    const tenantId = getTenantId(row);
    const updatedTenantList = buildUpdatedRolesForRoleChange(
      tenantList,
      tenantId,
      selection.value,
    );
    setUpdatingTenantId(tenantId);
    updateGroup(
      {
        groupId,
        roles: updatedTenantList,
      },
      {
        onSuccess: () => {
          setUpdatingTenantId(null);
          toast({
            status: 'success',
            description: `Team's access for ${row.title} App updated successfully`,
          });
        },
        onError: () => {
          setUpdatingTenantId(null);
          toast({
            status: 'error',
            description: `Failed to update team's access for ${row.title} App`,
          });
        },
      },
    );
  };

  const removeProductHandler = (removeIndex: number) => {
    setSelectedRemoveIndex(removeIndex);
    onOpen();
  };

  const confirmRemoveProduct = () => {
    if (selectedRemoveIndex === null) return;
    const removedRow = productAccessRowState[selectedRemoveIndex];
    const removedTenantId = getTenantId(removedRow);
    const updatedTenantList = buildUpdatedRolesForRemoval(
      tenantList,
      removedTenantId,
    );
    setIsRemoving(true);
    updateGroup(
      {
        groupId,
        roles: updatedTenantList,
      },
      {
        onSuccess: () => {
          setIsRemoving(false);
          toast({
            status: 'success',
            description: 'App removed',
          });
          onClose();
          setSelectedRemoveIndex(null);
        },
        onError: () => {
          setIsRemoving(false);
          toast({
            status: 'error',
            description: 'Failed to remove app',
          });
          onClose();
          setSelectedRemoveIndex(null);
        },
      },
    );
  };

  const selectedApp =
    selectedRemoveIndex !== null
      ? productAccessRowState[selectedRemoveIndex]
      : null;

  return (
    <>
      <Skeleton isLoaded={!isLoading}>
        {productAccessRowState.map(
          ({ id, title, subTitle, icon, roles, accessType }, index) => (
            <Stack
              spacing='4'
              direction={['column', 'row']}
              pb='4'
              alignItems='center'
              key={`${id}-${roles.length}`}
            >
              <Box w={'full'} data-testid='app-access'>
                <MediaCard
                  size='option'
                  key={id}
                  title={title}
                  imageUrl={icon.iconUrl}
                  label={subTitle}
                />
              </Box>
              <Box minW={44}>
                <ReactSelect
                  key={id}
                  aria-label={`${title} access role`}
                  selectedOptionColorScheme='primary'
                  name={`app-access-select-${id}`}
                  inputId={`app-access-select-input-id-${id}`}
                  classNamePrefix='app-access-select'
                  value={
                    (accessType &&
                      roles.find(
                        (r: RoleOption) => r.value === accessType.value,
                      )) ||
                    null
                  }
                  isDisabled={updatingTenantId === id}
                  onChange={(data: RoleOption | null) => {
                    onSelectAccessHandler(index, data);
                  }}
                  options={roles}
                />
              </Box>
              <IconButton
                variant='ghost'
                size='sm'
                aria-label='remove product'
                icon={<PortalIcon path={mdiClose} />}
                isLoading={updatingTenantId === id}
                onClick={() => removeProductHandler(index)}
              />
            </Stack>
          ),
        )}
      </Skeleton>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Remove app
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove{' '}
              <Text as='span' fontWeight='bold'>
                {selectedApp?.title}
              </Text>{' '}
              from this group?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant='ghost'
                ref={cancelRef}
                onClick={onClose}
                isLoading={isRemoving}
              >
                Cancel
              </Button>
              <Button
                colorScheme='red'
                onClick={confirmRemoveProduct}
                ml={3}
                isLoading={isRemoving}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
